import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, TrendingUp, List, Activity, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea, ReferenceLine,
} from 'recharts';
import api from '@/lib/axios';
import { cn } from '@/lib/utils';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { getErrorMessage } from '@/lib/errors';
import { useConfiguracionClinica, moduloAplica } from '@/hooks/useConfiguracionClinica';

// ─── Types ───────────────────────────────────────────────────────────────────

type Status = 'normal' | 'elevado' | 'alto' | 'critico' | 'bajo' | 'sin-dato';

interface Reading {
  id: string;
  fechaHora: string;
  presionSistolica: number | null;
  presionDiastolica: number | null;
  frecuenciaCardiaca: number | null;
  frecuenciaRespiratoria: number | null;
  temperatura: number | null;
  saturacionOxigeno: number | null;
  peso: number | null;
  talla: number | null;
  perimetroCefalico: number | null;
  perimetroAbdominal: number | null;
  glucosa: number | null;
  // Composición corporal y perfil deportivo
  nivelActividad: string | null;
  contextoMedicion: string | null;
  porcentajeGrasaCorporal: number | null;
  masaMuscular: number | null;
  masaGrasa: number | null;
  masaOsea: number | null;
  vo2Max: number | null;
  notas: string | null;
}

// ─── Status helpers ───────────────────────────────────────────────────────────

function paStatus(s: number, d: number): Status {
  if (s >= 180 || d >= 120) return 'critico';
  if (s >= 140 || d >= 90) return 'alto';
  if (s >= 130 || d >= 80) return 'elevado';
  if (s < 90 || d < 60) return 'bajo';
  return 'normal';
}
function fcStatus(v: number, postEjercicio = false): Status {
  if (postEjercicio) {
    // En ejercicio, FC hasta 170 es esperado; marcar critico solo si muy fuera de rango
    if (v > 200 || v < 40) return 'critico';
    return 'normal';
  }
  if (v > 150 || v < 40) return 'critico';
  if (v > 100 || v < 60) return 'elevado';
  return 'normal';
}
function frStatus(v: number, postEjercicio = false): Status {
  if (postEjercicio) {
    if (v > 50 || v < 8) return 'critico';
    return 'normal';
  }
  if (v > 30 || v < 8) return 'critico';
  if (v > 20 || v < 12) return 'elevado';
  return 'normal';
}
function tempStatus(v: number): Status {
  if (v >= 40 || v < 35) return 'critico';
  if (v >= 38.5) return 'alto';
  if (v >= 37.5) return 'elevado';
  if (v < 36) return 'bajo';
  return 'normal';
}
function spo2Status(v: number): Status {
  if (v < 90) return 'critico';
  if (v < 95) return 'elevado';
  return 'normal';
}
function imcStatus(v: number): Status {
  if (v >= 40) return 'critico';
  if (v >= 30) return 'alto';
  if (v >= 25) return 'elevado';
  if (v < 18.5) return 'bajo';
  return 'normal';
}
function glucosaStatus(v: number): Status {
  if (v >= 200 || v < 54) return 'critico';
  if (v >= 126 || v < 70) return 'alto';
  if (v >= 100) return 'elevado';
  return 'normal';
}
/** % grasa corporal — rangos generales (unisex).
 *  Atletas: 6-20%; fitness: 20-25%; promedio: 25-32%; obeso >32%.
 *  Valor <8% es peligrosamente bajo (grasa esencial). */
function grasaStatus(v: number): Status {
  if (v < 5) return 'critico'; // bajo esencial
  if (v >= 5 && v < 8) return 'bajo'; // mínimo atlético, monitorear
  if (v <= 20) return 'normal'; // rango atlético/fitness
  if (v <= 25) return 'elevado'; // aceptable
  if (v <= 32) return 'alto';
  return 'critico';
}
/** VO₂ máx — capacidad aeróbica. Mayor = mejor. */
function vo2MaxStatus(v: number): Status {
  if (v >= 55) return 'normal'; // élite/atleta
  if (v >= 45) return 'normal'; // activo
  if (v >= 35) return 'elevado'; // moderado
  if (v >= 25) return 'alto'; // bajo
  return 'critico'; // muy bajo
}

const STATUS_STYLES: Record<Status, { bg: string; text: string; border: string; badge: string; dot: string }> = {
  normal:     { bg: 'bg-emerald-50',  text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700',  dot: 'bg-emerald-500' },
  elevado:    { bg: 'bg-yellow-50',   text: 'text-yellow-700',  border: 'border-yellow-200',  badge: 'bg-yellow-100 text-yellow-700',   dot: 'bg-yellow-500' },
  alto:       { bg: 'bg-orange-50',   text: 'text-orange-700',  border: 'border-orange-200',  badge: 'bg-orange-100 text-orange-700',   dot: 'bg-orange-500' },
  critico:    { bg: 'bg-red-50',      text: 'text-red-700',     border: 'border-red-200',     badge: 'bg-red-100 text-red-700',         dot: 'bg-red-500' },
  bajo:       { bg: 'bg-blue-50',     text: 'text-blue-700',    border: 'border-blue-200',    badge: 'bg-blue-100 text-blue-700',       dot: 'bg-blue-500' },
  'sin-dato': { bg: 'bg-gray-50',     text: 'text-gray-500',    border: 'border-gray-200',    badge: 'bg-gray-100 text-gray-500',       dot: 'bg-gray-300' },
};

const STATUS_LABEL: Record<Status, string> = {
  normal: 'Normal', elevado: 'Elevado', alto: 'Alto', critico: 'Crítico', bajo: 'Bajo', 'sin-dato': '—',
};

const NIVEL_ACTIVIDAD_LABEL: Record<string, string> = {
  sedentario: 'Sedentario', ligero: 'Ligero', moderado: 'Moderado',
  activo: 'Activo', 'muy-activo': 'Muy activo', deportista: 'Deportista / Atleta',
};

// ─── Utils ────────────────────────────────────────────────────────────────────

function calcImc(peso: number | null, talla: number | null): number | null {
  if (!peso || !talla || talla < 50) return null;
  return Math.round((peso / Math.pow(talla / 100, 2)) * 10) / 10;
}

function imcLabel(v: number): string {
  if (v < 18.5) return 'Bajo peso';
  if (v < 25) return 'Normal';
  if (v < 30) return 'Sobrepeso';
  if (v < 35) return 'Obesidad I';
  if (v < 40) return 'Obesidad II';
  return 'Obesidad III';
}

function grasaLabel(v: number): string {
  if (v < 8) return 'Grasa mínima';
  if (v <= 13) return 'Atlético ♂';
  if (v <= 20) return 'Atlético / Fitness';
  if (v <= 25) return 'Aceptable';
  if (v <= 32) return 'Por encima promedio';
  return 'Obesidad';
}

function vo2MaxLabel(v: number): string {
  if (v >= 60) return 'Élite';
  if (v >= 55) return 'Superior';
  if (v >= 45) return 'Excelente';
  if (v >= 35) return 'Bueno';
  if (v >= 25) return 'Regular';
  return 'Bajo';
}

// ─── Trend arrow ─────────────────────────────────────────────────────────────

function Trend({ curr, prev, invertColors = false }: { curr: number | null; prev: number | null; invertColors?: boolean }) {
  if (curr == null || prev == null) return null;
  const diff = curr - prev;
  if (Math.abs(diff) < 0.1) return <span className="text-gray-400 text-xs ml-1">→</span>;
  const up = diff > 0;
  // For metrics where higher is better (VO2, muscle mass), invertColors=true makes ↑ green
  const upColor = invertColors ? 'text-emerald-500' : 'text-orange-500';
  const downColor = invertColors ? 'text-orange-500' : 'text-emerald-500';
  return up
    ? <span className={cn('text-xs ml-1', upColor)}>↑ +{Math.abs(diff).toFixed(1)}</span>
    : <span className={cn('text-xs ml-1', downColor)}>↓ -{Math.abs(diff).toFixed(1)}</span>;
}

// ─── Vital card ──────────────────────────────────────────────────────────────

interface VitalCardProps {
  label: string;
  value: string | null;
  unit?: string;
  status: Status;
  trend?: React.ReactNode;
  sub?: string;
  emoji: string;
  wide?: boolean;
  badge?: React.ReactNode;
}

function VitalCard({ label, value, unit, status, trend, sub, emoji, wide, badge }: VitalCardProps) {
  const s = STATUS_STYLES[value != null ? status : 'sin-dato'];
  return (
    <div className={cn('rounded-xl border p-3 flex flex-col gap-1 transition-shadow hover:shadow-sm', s.bg, s.border, wide && 'col-span-2')}>
      <div className="flex items-center justify-between">
        <span className="text-base">{emoji}</span>
        {badge ?? (value != null && (
          <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full', s.badge)}>
            {STATUS_LABEL[status]}
          </span>
        ))}
      </div>
      <div>
        {value != null ? (
          <div className="flex items-baseline gap-0.5 flex-wrap">
            <span className={cn('text-lg font-bold leading-none', s.text)}>{value}</span>
            {unit && <span className="text-[11px] text-gray-500 ml-0.5">{unit}</span>}
            {trend}
          </div>
        ) : <span className="text-sm text-gray-400">—</span>}
        {sub && value != null && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
      </div>
      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide leading-none">{label}</p>
    </div>
  );
}

// ─── Athlete IMC card ─────────────────────────────────────────────────────────

function AthleteImcCard({ imc, prevImc, hasGrasa }: { imc: number | null; prevImc: number | null; hasGrasa: boolean }) {
  if (!imc) return null;
  return (
    <div className="rounded-xl border bg-violet-50 border-violet-200 p-3 flex flex-col gap-1 col-span-2 hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between">
        <span className="text-base">📊</span>
        <span className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">
          <Zap size={9} /> Atleta
        </span>
      </div>
      <div className="flex items-baseline gap-0.5">
        <span className="text-lg font-bold text-violet-700">{imc}</span>
        <span className="text-[11px] text-gray-500 ml-0.5">kg/m²</span>
        <Trend curr={imc} prev={prevImc} />
      </div>
      <p className="text-[10px] text-violet-600 leading-snug">
        {hasGrasa ? 'IMC informativo — usa % grasa corporal como referencia' : 'En deportistas el IMC sobreestima la grasa. Registra % grasa corporal para valoración precisa.'}
      </p>
      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide leading-none">IMC</p>
    </div>
  );
}

// ─── Chart card ──────────────────────────────────────────────────────────────

interface ChartCardProps {
  label: string;
  unit: string;
  emoji: string;
  data: { fecha: string; value: number | null }[];
  normalMin?: number;
  normalMax?: number;
  color: string;
  domain?: [number, number];
}

function fmtChartDate(iso: string) {
  try { return format(new Date(iso), 'd MMM', { locale: es }); }
  catch { return iso; }
}

function ChartCard({ label, unit, emoji, data, normalMin, normalMax, color, domain }: ChartCardProps) {
  const points = data.filter(d => d.value != null).map(d => ({ fecha: fmtChartDate(d.fecha), value: d.value as number }));
  if (points.length < 2) return null;
  const values = points.map(p => p.value);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const pad = Math.max((maxV - minV) * 0.25, 2);
  const yDomain = domain ?? [Math.floor(minV - pad), Math.ceil(maxV + pad)];
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-sm">{emoji}</span>
        <p className="text-xs font-semibold text-text-primary">{label}</p>
        <span className="text-[10px] text-text-secondary ml-auto">{unit}</span>
      </div>
      <ResponsiveContainer width="100%" height={110}>
        <LineChart data={points} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          {normalMin != null && normalMax != null && (
            <ReferenceArea y1={normalMin} y2={normalMax} fill="#d1fae5" fillOpacity={0.5} />
          )}
          <XAxis dataKey="fecha" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
          <YAxis domain={yDomain} tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={32} />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb', padding: '4px 10px' }}
            labelStyle={{ fontWeight: 600 }}
            formatter={(v: number) => [`${v} ${unit}`, label]}
          />
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2}
            dot={{ r: 3, fill: color, strokeWidth: 0 }} activeDot={{ r: 4 }} />
          {normalMin != null && <ReferenceLine y={normalMin} stroke={color} strokeDasharray="4 2" strokeOpacity={0.35} />}
          {normalMax != null && <ReferenceLine y={normalMax} stroke={color} strokeDasharray="4 2" strokeOpacity={0.35} />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Dual PA chart ───────────────────────────────────────────────────────────

function PaChartCard({ data }: { data: { fecha: string; s: number | null; d: number | null }[] }) {
  const points = data.filter(d => d.s != null && d.d != null)
    .map(d => ({ fecha: fmtChartDate(d.fecha), sistolica: d.s as number, diastolica: d.d as number }));
  if (points.length < 2) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-sm">🩺</span>
        <p className="text-xs font-semibold text-text-primary">Presión Arterial</p>
        <span className="text-[10px] text-text-secondary ml-auto">mmHg</span>
      </div>
      <ResponsiveContainer width="100%" height={110}>
        <LineChart data={points} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
          <ReferenceArea y1={60} y2={80} fill="#d1fae5" fillOpacity={0.3} />
          <ReferenceArea y1={100} y2={120} fill="#d1fae5" fillOpacity={0.3} />
          <XAxis dataKey="fecha" tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
          <YAxis domain={[40, 200]} tick={{ fontSize: 9 }} tickLine={false} axisLine={false} width={32} />
          <Tooltip
            contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb', padding: '4px 10px' }}
            formatter={(v: number, name: string) => [`${v} mmHg`, name === 'sistolica' ? 'Sistólica' : 'Diastólica']}
          />
          <Line type="monotone" dataKey="sistolica" stroke="#ef4444" strokeWidth={2} dot={{ r: 3, fill: '#ef4444', strokeWidth: 0 }} activeDot={{ r: 4 }} />
          <Line type="monotone" dataKey="diastolica" stroke="#f97316" strokeWidth={2} dot={{ r: 3, fill: '#f97316', strokeWidth: 0 }} activeDot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex items-center gap-3 mt-1.5">
        <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /><span className="text-[9px] text-gray-500">Sistólica</span></div>
        <div className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /><span className="text-[9px] text-gray-500">Diastólica</span></div>
      </div>
    </div>
  );
}

// ─── History row ─────────────────────────────────────────────────────────────

function HistoryRow({ r, prev }: { r: Reading; prev: Reading | null }) {
  const [open, setOpen] = useState(false);
  const imc = calcImc(r.peso, r.talla);
  const prevImc = calcImc(prev?.peso ?? null, prev?.talla ?? null);
  const isDeportista = r.nivelActividad === 'deportista';
  const isPostEjercicio = r.contextoMedicion === 'post-ejercicio';

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors text-left">
        <div className="flex items-center gap-2">
          {open ? <ChevronUp size={13} className="text-text-secondary" /> : <ChevronDown size={13} className="text-text-secondary" />}
          <div>
            <p className="text-xs font-semibold text-text-primary">
              {format(new Date(r.fechaHora), "d 'de' MMMM yyyy, HH:mm", { locale: es })}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <p className="text-[11px] text-text-secondary">
                {formatDistanceToNow(new Date(r.fechaHora), { addSuffix: true, locale: es })}
              </p>
              {isDeportista && (
                <span className="text-[9px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5">
                  <Zap size={8} /> Atleta
                </span>
              )}
              {isPostEjercicio && (
                <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">
                  ⚡ Post-ejercicio
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {r.presionSistolica && r.presionDiastolica && (
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
              PA {r.presionSistolica}/{r.presionDiastolica}
            </span>
          )}
          {r.frecuenciaCardiaca && (
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">FC {r.frecuenciaCardiaca}</span>
          )}
          {r.temperatura && (
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{r.temperatura}°C</span>
          )}
          {r.saturacionOxigeno && (
            <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">SpO₂ {r.saturacionOxigeno}%</span>
          )}
          {r.porcentajeGrasaCorporal && (
            <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full">Grasa {r.porcentajeGrasaCorporal}%</span>
          )}
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50/50">
          <div className="grid grid-cols-3 gap-1.5 mt-2.5 text-xs">
            {r.presionSistolica && r.presionDiastolica && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Presión arterial</p>
                <p className="font-semibold text-text-primary">{r.presionSistolica}/{r.presionDiastolica} <span className="text-[10px] font-normal">mmHg</span></p>
              </div>
            )}
            {r.frecuenciaCardiaca && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Frec. cardíaca</p>
                <p className="font-semibold text-text-primary">{r.frecuenciaCardiaca} <span className="text-[10px] font-normal">lpm</span><Trend curr={r.frecuenciaCardiaca} prev={prev?.frecuenciaCardiaca ?? null} /></p>
              </div>
            )}
            {r.frecuenciaRespiratoria && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Frec. respiratoria</p>
                <p className="font-semibold text-text-primary">{r.frecuenciaRespiratoria} <span className="text-[10px] font-normal">rpm</span><Trend curr={r.frecuenciaRespiratoria} prev={prev?.frecuenciaRespiratoria ?? null} /></p>
              </div>
            )}
            {r.temperatura && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Temperatura</p>
                <p className="font-semibold text-text-primary">{r.temperatura} <span className="text-[10px] font-normal">°C</span><Trend curr={r.temperatura} prev={prev?.temperatura ?? null} /></p>
              </div>
            )}
            {r.saturacionOxigeno && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">SpO₂</p>
                <p className="font-semibold text-text-primary">{r.saturacionOxigeno} <span className="text-[10px] font-normal">%</span><Trend curr={r.saturacionOxigeno} prev={prev?.saturacionOxigeno ?? null} /></p>
              </div>
            )}
            {r.peso && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Peso</p>
                <p className="font-semibold text-text-primary">{r.peso} <span className="text-[10px] font-normal">kg</span><Trend curr={r.peso} prev={prev?.peso ?? null} /></p>
              </div>
            )}
            {r.talla && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Talla</p>
                <p className="font-semibold text-text-primary">{r.talla} <span className="text-[10px] font-normal">cm</span></p>
              </div>
            )}
            {imc != null && (
              <div className={cn('bg-white rounded-lg px-2 py-1.5 border', isDeportista ? 'border-violet-200 bg-violet-50' : 'border-gray-100')}>
                <p className="text-[10px] text-text-secondary">IMC {isDeportista && '⚡'}</p>
                <p className={cn('font-semibold', isDeportista ? 'text-violet-700' : 'text-text-primary')}>{imc} <span className="text-[10px] font-normal">kg/m²</span><Trend curr={imc} prev={prevImc} /></p>
                {isDeportista && <p className="text-[9px] text-violet-500">Atleta — ver % grasa</p>}
              </div>
            )}
            {r.glucosa && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Glucosa</p>
                <p className="font-semibold text-text-primary">{r.glucosa} <span className="text-[10px] font-normal">mg/dL</span><Trend curr={r.glucosa} prev={prev?.glucosa ?? null} /></p>
              </div>
            )}
            {r.perimetroAbdominal && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Per. abdominal</p>
                <p className="font-semibold text-text-primary">{r.perimetroAbdominal} <span className="text-[10px] font-normal">cm</span><Trend curr={r.perimetroAbdominal} prev={prev?.perimetroAbdominal ?? null} /></p>
              </div>
            )}
            {r.perimetroCefalico && (
              <div className="bg-white rounded-lg px-2 py-1.5 border border-gray-100">
                <p className="text-[10px] text-text-secondary">Per. cefálico</p>
                <p className="font-semibold text-text-primary">{r.perimetroCefalico} <span className="text-[10px] font-normal">cm</span></p>
              </div>
            )}
            {/* Composición corporal */}
            {r.porcentajeGrasaCorporal != null && (
              <div className="bg-violet-50 rounded-lg px-2 py-1.5 border border-violet-100">
                <p className="text-[10px] text-violet-600">% Grasa corporal</p>
                <p className="font-semibold text-violet-700">{r.porcentajeGrasaCorporal} <span className="text-[10px] font-normal">%</span><Trend curr={r.porcentajeGrasaCorporal} prev={prev?.porcentajeGrasaCorporal ?? null} /></p>
              </div>
            )}
            {r.masaMuscular != null && (
              <div className="bg-violet-50 rounded-lg px-2 py-1.5 border border-violet-100">
                <p className="text-[10px] text-violet-600">Masa muscular</p>
                <p className="font-semibold text-violet-700">{r.masaMuscular} <span className="text-[10px] font-normal">kg</span><Trend curr={r.masaMuscular} prev={prev?.masaMuscular ?? null} invertColors /></p>
              </div>
            )}
            {r.masaGrasa != null && (
              <div className="bg-violet-50 rounded-lg px-2 py-1.5 border border-violet-100">
                <p className="text-[10px] text-violet-600">Masa grasa</p>
                <p className="font-semibold text-violet-700">{r.masaGrasa} <span className="text-[10px] font-normal">kg</span><Trend curr={r.masaGrasa} prev={prev?.masaGrasa ?? null} /></p>
              </div>
            )}
            {r.masaOsea != null && (
              <div className="bg-violet-50 rounded-lg px-2 py-1.5 border border-violet-100">
                <p className="text-[10px] text-violet-600">Masa ósea</p>
                <p className="font-semibold text-violet-700">{r.masaOsea} <span className="text-[10px] font-normal">kg</span></p>
              </div>
            )}
            {r.vo2Max != null && (
              <div className="bg-violet-50 rounded-lg px-2 py-1.5 border border-violet-100">
                <p className="text-[10px] text-violet-600">VO₂ Máx</p>
                <p className="font-semibold text-violet-700">{r.vo2Max} <span className="text-[10px] font-normal">mL/kg/min</span><Trend curr={r.vo2Max} prev={prev?.vo2Max ?? null} invertColors /></p>
              </div>
            )}
          </div>
          {r.nivelActividad && (
            <p className="text-[11px] text-text-secondary mt-2">
              Nivel de actividad: <span className="font-medium">{NIVEL_ACTIVIDAD_LABEL[r.nivelActividad] ?? r.nivelActividad}</span>
              {r.contextoMedicion && <> · Contexto: <span className="font-medium capitalize">{r.contextoMedicion.replace('-', ' ')}</span></>}
            </p>
          )}
          {r.notas && <p className="text-[11px] text-text-secondary mt-2 italic border-t border-gray-100 pt-2">{r.notas}</p>}
          <p className="text-[10px] text-gray-400 mt-2 border-t border-gray-100 pt-2">
            Registro inmutable (NOM-004). Si hubo un error, registra una nueva medición.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Form modal ──────────────────────────────────────────────────────────────

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

function toLocalDatetimeValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface ModalProps { onClose: () => void; onSave: (dto: any) => void; isPending: boolean; puedeVerDeportivo: boolean }

function NuevaMedicionModal({ onClose, onSave, isPending, puedeVerDeportivo }: ModalProps) {
  const [form, setForm] = useState({
    fechaHora: toLocalDatetimeValue(new Date()),
    nivelActividad: '', contextoMedicion: '',
    presionSistolica: '', presionDiastolica: '',
    frecuenciaCardiaca: '', frecuenciaRespiratoria: '',
    temperatura: '', saturacionOxigeno: '',
    peso: '', talla: '',
    glucosa: '', perimetroCefalico: '', perimetroAbdominal: '',
    porcentajeGrasaCorporal: '', masaMuscular: '', masaGrasa: '', masaOsea: '',
    vo2Max: '',
    notas: '',
  });

  const imc = useMemo(() => {
    const p = parseFloat(form.peso);
    const t = parseFloat(form.talla);
    return calcImc(isNaN(p) ? null : p, isNaN(t) ? null : t);
  }, [form.peso, form.talla]);

  const f = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const dto: Record<string, any> = { fechaHora: form.fechaHora };
    if (form.nivelActividad) dto.nivelActividad = form.nivelActividad;
    if (form.contextoMedicion) dto.contextoMedicion = form.contextoMedicion;
    const nums = [
      'presionSistolica', 'presionDiastolica', 'frecuenciaCardiaca', 'frecuenciaRespiratoria',
      'temperatura', 'saturacionOxigeno', 'peso', 'talla', 'glucosa',
      'perimetroCefalico', 'perimetroAbdominal',
      'porcentajeGrasaCorporal', 'masaMuscular', 'masaGrasa', 'masaOsea', 'vo2Max',
    ];
    for (const k of nums) {
      const v = parseFloat((form as any)[k]);
      if (!isNaN(v) && v > 0) dto[k] = v;
    }
    if (form.notas.trim()) dto.notas = form.notas.trim();
    const filled = nums.some(k => !isNaN(parseFloat((form as any)[k])));
    if (!filled) return toast.error('Ingresa al menos un signo vital');
    onSave(dto);
  }

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-2">{children}</p>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-text-primary flex items-center gap-2">
            <Activity size={16} className="text-primary-600" /> Nueva medición
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Fecha */}
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1 uppercase tracking-wide">Fecha y hora</label>
            <input type="datetime-local" className={inputCls} value={form.fechaHora} onChange={f('fechaHora')} />
          </div>

          {/* Cardiovascular */}
          <div>
            <SectionTitle>Cardiovascular</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">🩺 Presión sistólica (mmHg)</label>
                <input type="number" className={inputCls} placeholder="120" min={40} max={300} value={form.presionSistolica} onChange={f('presionSistolica')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">🩺 Presión diastólica (mmHg)</label>
                <input type="number" className={inputCls} placeholder="80" min={20} max={200} value={form.presionDiastolica} onChange={f('presionDiastolica')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">❤️ Frec. cardíaca (lpm)</label>
                <input type="number" className={inputCls} placeholder="72" min={20} max={300} value={form.frecuenciaCardiaca} onChange={f('frecuenciaCardiaca')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">🫁 SpO₂ (%)</label>
                <input type="number" className={inputCls} placeholder="98" min={50} max={100} step={0.1} value={form.saturacionOxigeno} onChange={f('saturacionOxigeno')} />
              </div>
            </div>
          </div>

          {/* Respiratorio + Temperatura */}
          <div>
            <SectionTitle>Respiratorio y temperatura</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">💨 Frec. respiratoria (rpm)</label>
                <input type="number" className={inputCls} placeholder="16" min={4} max={60} value={form.frecuenciaRespiratoria} onChange={f('frecuenciaRespiratoria')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">🌡️ Temperatura (°C)</label>
                <input type="number" className={inputCls} placeholder="36.5" min={30} max={45} step={0.1} value={form.temperatura} onChange={f('temperatura')} />
              </div>
            </div>
          </div>

          {/* Antropometría */}
          <div>
            <SectionTitle>Antropometría</SectionTitle>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">⚖️ Peso (kg)</label>
                <input type="number" className={inputCls} placeholder="70.0" min={0.5} max={500} step={0.1} value={form.peso} onChange={f('peso')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">📏 Talla (cm)</label>
                <input type="number" className={inputCls} placeholder="170" min={20} max={250} step={0.1} value={form.talla} onChange={f('talla')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">📊 IMC (calculado)</label>
                <div className={cn(inputCls, 'bg-gray-50 text-text-secondary cursor-default flex items-center')}>
                  {imc != null ? <span className="font-semibold text-text-primary">{imc} <span className="text-[10px] font-normal">kg/m²</span></span> : <span className="text-gray-400">—</span>}
                </div>
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">🎯 Per. abdominal (cm)</label>
                <input type="number" className={inputCls} placeholder="85" min={20} max={250} step={0.1} value={form.perimetroAbdominal} onChange={f('perimetroAbdominal')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">📐 Per. cefálico (cm)</label>
                <input type="number" className={inputCls} placeholder="55" min={20} max={80} step={0.1} value={form.perimetroCefalico} onChange={f('perimetroCefalico')} />
              </div>
            </div>
          </div>

          {/* Metabólico */}
          <div>
            <SectionTitle>Metabólico</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">🩸 Glucosa (mg/dL)</label>
                <input type="number" className={inputCls} placeholder="90" min={10} max={600} step={0.1} value={form.glucosa} onChange={f('glucosa')} />
              </div>
            </div>
          </div>

          {/* Composición corporal y perfil deportivo — oculto para perfiles sin signos_vitales_deportivo */}
          {puedeVerDeportivo && (
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Zap size={13} className="text-violet-600" />
              <p className="text-[10px] font-bold text-violet-700 uppercase tracking-widest">Composición corporal y perfil deportivo</p>
            </div>
            <p className="text-[10px] text-violet-600 -mt-1">
              Para atletas y deportistas, el IMC puede sobreestimar la grasa corporal.
              Registra % de grasa para una valoración más precisa.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Nivel de actividad</label>
                <select className={inputCls} value={form.nivelActividad} onChange={f('nivelActividad')}>
                  <option value="">Sin especificar</option>
                  <option value="sedentario">Sedentario</option>
                  <option value="ligero">Ligero (1–2 días/sem)</option>
                  <option value="moderado">Moderado (3–5 días/sem)</option>
                  <option value="activo">Activo (6–7 días/sem)</option>
                  <option value="muy-activo">Muy activo (2× día)</option>
                  <option value="deportista">Deportista / Atleta</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Contexto de medición</label>
                <select className={inputCls} value={form.contextoMedicion} onChange={f('contextoMedicion')}>
                  <option value="">Sin especificar</option>
                  <option value="reposo">Reposo</option>
                  <option value="post-ejercicio">Post-ejercicio</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-text-secondary mb-1">% Grasa corporal</label>
                <input type="number" className={inputCls} placeholder="15.0" min={1} max={70} step={0.1} value={form.porcentajeGrasaCorporal} onChange={f('porcentajeGrasaCorporal')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Masa muscular (kg)</label>
                <input type="number" className={inputCls} placeholder="32.0" min={1} max={200} step={0.1} value={form.masaMuscular} onChange={f('masaMuscular')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Masa grasa (kg)</label>
                <input type="number" className={inputCls} placeholder="10.0" min={0.5} max={200} step={0.1} value={form.masaGrasa} onChange={f('masaGrasa')} />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Masa ósea (kg)</label>
                <input type="number" className={inputCls} placeholder="3.0" min={0.5} max={20} step={0.1} value={form.masaOsea} onChange={f('masaOsea')} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-text-secondary mb-1">VO₂ Máx (mL/kg/min) — Capacidad aeróbica</label>
                <input type="number" className={inputCls} placeholder="45" min={5} max={100} step={0.1} value={form.vo2Max} onChange={f('vo2Max')} />
              </div>
            </div>
          </div>
          )}

          {/* Notas */}
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1 uppercase tracking-wide">Notas adicionales</label>
            <textarea className={cn(inputCls, 'resize-none')} rows={2}
              placeholder="Observaciones, condición al momento de la medición..." value={form.notas} onChange={f('notas')} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-text-secondary py-2.5 rounded-lg text-sm hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={isPending}
              className="flex-1 bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors">
              {isPending ? 'Guardando...' : 'Guardar medición'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main tab ────────────────────────────────────────────────────────────────

interface Props { id: string; canEdit: boolean; noDelete?: boolean; triggerAdd?: number }

export function SignosVitalesTab({ id, canEdit, triggerAdd }: Props) {
  const qc = useQueryClient();
  const { perfilTipo, modulosHabilitados } = useConfiguracionClinica();
  const puedeVerDeportivo = moduloAplica('signos_vitales_deportivo', perfilTipo, modulosHabilitados);
  const [vista, setVista] = useState<'resumen' | 'cronologico'>('resumen');
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => { if (triggerAdd) setModalOpen(true); }, [triggerAdd]);

  const { data: readings = [], isLoading } = useQuery({
    queryKey: ['signos-vitales', id],
    queryFn: () => api.get(`/api/pacientes/${id}/signos-vitales`).then(r => r.data as Reading[]),
  });

  const createMutation = useMutation({
    mutationFn: (dto: any) => api.post(`/api/pacientes/${id}/signos-vitales`, dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['signos-vitales', id] }); toast.success('Medición registrada'); setModalOpen(false); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar la medición')),
  });

  const latest = readings[0] ?? null;
  const prev = readings[1] ?? null;
  const imc = calcImc(latest?.peso ?? null, latest?.talla ?? null);
  const prevImc = calcImc(prev?.peso ?? null, prev?.talla ?? null);

  const isDeportista = latest?.nivelActividad === 'deportista';
  const isPostEjercicio = latest?.contextoMedicion === 'post-ejercicio';

  const paStatusVal = (latest?.presionSistolica && latest?.presionDiastolica)
    ? paStatus(Number(latest.presionSistolica), Number(latest.presionDiastolica))
    : 'sin-dato';

  const chronoData = useMemo(() => [...readings].reverse(), [readings]);

  if (isLoading) return <p className="text-center py-10 text-sm text-text-secondary">Cargando...</p>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 p-0.5 bg-gray-100 rounded-lg">
          <button type="button" onClick={() => setVista('resumen')}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              vista === 'resumen' ? 'bg-white shadow-sm text-text-primary' : 'text-text-secondary hover:text-text-primary')}>
            <List size={12} /> Resumen
          </button>
          <button type="button" onClick={() => setVista('cronologico')}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              vista === 'cronologico' ? 'bg-white shadow-sm text-text-primary' : 'text-text-secondary hover:text-text-primary')}>
            <TrendingUp size={12} /> Cronológico
          </button>
        </div>
        {canEdit && (
          <button type="button" onClick={() => setModalOpen(true)}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors">
            <Plus size={13} /> Registrar medición
          </button>
        )}
      </div>

      {readings.length === 0 ? (
        <div className="text-center py-14 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <Activity size={32} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-medium text-text-secondary">Sin mediciones registradas</p>
          <p className="text-xs text-gray-400 mt-1">Registra la primera medición de signos vitales</p>
          {canEdit && (
            <button type="button" onClick={() => setModalOpen(true)}
              className="mt-4 flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors mx-auto">
              <Plus size={13} /> Registrar medición
            </button>
          )}
        </div>
      ) : vista === 'resumen' ? (
        <>
          {/* Timestamp + context badges */}
          {latest && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block flex-shrink-0" />
              <p className="text-xs text-text-secondary">
                Última medición: <span className="font-medium text-text-primary">
                  {formatDistanceToNow(new Date(latest.fechaHora), { addSuffix: true, locale: es })}
                </span>
                {' · '}{format(new Date(latest.fechaHora), "d MMM yyyy, HH:mm", { locale: es })}
              </p>
              {latest.nivelActividad && (
                <span className={cn(
                  'text-[10px] font-medium px-2 py-0.5 rounded-full border',
                  isDeportista
                    ? 'bg-violet-50 text-violet-700 border-violet-200'
                    : 'bg-gray-50 text-gray-600 border-gray-200'
                )}>
                  {isDeportista && <Zap size={9} className="inline mr-0.5" />}
                  {NIVEL_ACTIVIDAD_LABEL[latest.nivelActividad] ?? latest.nivelActividad}
                </span>
              )}
            </div>
          )}

          {/* Post-exercise banner */}
          {isPostEjercicio && (
            <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5">
              <span className="text-lg flex-shrink-0">⚡</span>
              <div>
                <p className="text-xs font-semibold text-amber-800">Medición post-ejercicio</p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  Los valores cardiovasculares (FC, FR, PA) pueden estar elevados de forma fisiológica. Comparar con mediciones en reposo.
                </p>
              </div>
            </div>
          )}

          {/* Athlete IMC note */}
          {isDeportista && !isPostEjercicio && (
            <div className="flex items-start gap-2.5 bg-violet-50 border border-violet-200 rounded-xl px-3.5 py-2.5">
              <Zap size={14} className="text-violet-600 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-violet-700">
                <span className="font-semibold">Perfil deportista activo.</span>{' '}
                El IMC puede sobreestimar la grasa corporal debido a la alta masa muscular.
                El porcentaje de grasa corporal es el indicador primario de composición en este perfil.
              </p>
            </div>
          )}

          {/* Vital sign cards */}
          {latest && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {/* PA — wide */}
              <div className={cn(
                'rounded-xl border p-3 flex flex-col gap-1 col-span-2 transition-shadow hover:shadow-sm',
                STATUS_STYLES[paStatusVal].bg, STATUS_STYLES[paStatusVal].border
              )}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-base">🩺</span>
                    {isPostEjercicio && <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Post-ej.</span>}
                  </div>
                  {latest.presionSistolica && latest.presionDiastolica && !isPostEjercicio && (
                    <span className={cn('text-[10px] font-semibold px-1.5 py-0.5 rounded-full', STATUS_STYLES[paStatusVal].badge)}>
                      {STATUS_LABEL[paStatusVal]}
                    </span>
                  )}
                </div>
                {latest.presionSistolica && latest.presionDiastolica ? (
                  <div className="flex items-baseline gap-1">
                    <span className={cn('text-2xl font-bold', STATUS_STYLES[paStatusVal].text)}>{latest.presionSistolica}</span>
                    <span className="text-lg font-bold text-gray-400">/</span>
                    <span className={cn('text-2xl font-bold', STATUS_STYLES[paStatusVal].text)}>{latest.presionDiastolica}</span>
                    <span className="text-[11px] text-gray-500 ml-1">mmHg</span>
                  </div>
                ) : <span className="text-sm text-gray-400">—</span>}
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Presión Arterial</p>
              </div>

              <VitalCard
                label="Frec. Cardíaca" emoji="❤️" unit="lpm"
                value={latest.frecuenciaCardiaca != null ? String(latest.frecuenciaCardiaca) : null}
                status={latest.frecuenciaCardiaca ? fcStatus(Number(latest.frecuenciaCardiaca), isPostEjercicio) : 'sin-dato'}
                trend={<Trend curr={latest.frecuenciaCardiaca} prev={prev?.frecuenciaCardiaca ?? null} />}
                sub={isPostEjercicio ? 'Post-ejercicio' : 'Normal: 60–100 lpm'}
                badge={isPostEjercicio && latest.frecuenciaCardiaca ? <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Post-ej.</span> : undefined}
              />
              <VitalCard
                label="Temperatura" emoji="🌡️" unit="°C"
                value={latest.temperatura != null ? String(latest.temperatura) : null}
                status={latest.temperatura ? tempStatus(Number(latest.temperatura)) : 'sin-dato'}
                trend={<Trend curr={latest.temperatura} prev={prev?.temperatura ?? null} />}
                sub="Normal: 36–37.4°C"
              />
              <VitalCard
                label="SpO₂" emoji="🫁" unit="%"
                value={latest.saturacionOxigeno != null ? String(latest.saturacionOxigeno) : null}
                status={latest.saturacionOxigeno ? spo2Status(Number(latest.saturacionOxigeno)) : 'sin-dato'}
                trend={<Trend curr={latest.saturacionOxigeno} prev={prev?.saturacionOxigeno ?? null} />}
                sub="Normal: ≥95%"
              />
              <VitalCard
                label="Frec. Respiratoria" emoji="💨" unit="rpm"
                value={latest.frecuenciaRespiratoria != null ? String(latest.frecuenciaRespiratoria) : null}
                status={latest.frecuenciaRespiratoria ? frStatus(Number(latest.frecuenciaRespiratoria), isPostEjercicio) : 'sin-dato'}
                trend={<Trend curr={latest.frecuenciaRespiratoria} prev={prev?.frecuenciaRespiratoria ?? null} />}
                sub={isPostEjercicio ? 'Post-ejercicio' : 'Normal: 12–20 rpm'}
                badge={isPostEjercicio && latest.frecuenciaRespiratoria ? <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Post-ej.</span> : undefined}
              />
              <VitalCard
                label="Peso" emoji="⚖️" unit="kg"
                value={latest.peso != null ? String(latest.peso) : null}
                status="normal"
                trend={<Trend curr={latest.peso} prev={prev?.peso ?? null} />}
              />
              <VitalCard
                label="Talla" emoji="📏" unit="cm"
                value={latest.talla != null ? String(latest.talla) : null}
                status="normal"
              />

              {/* IMC — context-aware */}
              {imc != null && (
                isDeportista ? (
                  <AthleteImcCard imc={imc} prevImc={prevImc} hasGrasa={latest.porcentajeGrasaCorporal != null} />
                ) : (
                  <VitalCard
                    label="IMC" emoji="📊" unit="kg/m²"
                    value={String(imc)}
                    status={imcStatus(imc)}
                    trend={<Trend curr={imc} prev={prevImc} />}
                    sub={imcLabel(imc)}
                  />
                )
              )}

              <VitalCard
                label="Glucosa" emoji="🩸" unit="mg/dL"
                value={latest.glucosa != null ? String(latest.glucosa) : null}
                status={latest.glucosa ? glucosaStatus(Number(latest.glucosa)) : 'sin-dato'}
                trend={<Trend curr={latest.glucosa} prev={prev?.glucosa ?? null} />}
                sub="Normal: 70–100 mg/dL"
              />
              {latest.perimetroAbdominal && (
                <VitalCard label="Per. abdominal" emoji="🎯" unit="cm"
                  value={String(latest.perimetroAbdominal)} status="normal"
                  trend={<Trend curr={latest.perimetroAbdominal} prev={prev?.perimetroAbdominal ?? null} />}
                />
              )}
              {latest.perimetroCefalico && (
                <VitalCard label="Per. cefálico" emoji="📐" unit="cm"
                  value={String(latest.perimetroCefalico)} status="normal"
                />
              )}

              {/* Composición corporal (sección deportiva) — oculta para perfiles sin signos_vitales_deportivo */}
              {puedeVerDeportivo && (latest.porcentajeGrasaCorporal != null || latest.masaMuscular != null ||
                latest.masaGrasa != null || latest.masaOsea != null || latest.vo2Max != null) && (
                <div className="col-span-2 sm:col-span-3 mt-1">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Zap size={11} className="text-violet-600" />
                    <p className="text-[10px] font-bold text-violet-700 uppercase tracking-widest">Composición corporal</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {latest.porcentajeGrasaCorporal != null && (
                      <VitalCard
                        label="% Grasa corporal" emoji="🔬" unit="%"
                        value={String(latest.porcentajeGrasaCorporal)}
                        status={grasaStatus(Number(latest.porcentajeGrasaCorporal))}
                        trend={<Trend curr={latest.porcentajeGrasaCorporal} prev={prev?.porcentajeGrasaCorporal ?? null} />}
                        sub={grasaLabel(Number(latest.porcentajeGrasaCorporal))}
                      />
                    )}
                    {latest.masaMuscular != null && (
                      <VitalCard
                        label="Masa muscular" emoji="💪" unit="kg"
                        value={String(latest.masaMuscular)} status="normal"
                        trend={<Trend curr={latest.masaMuscular} prev={prev?.masaMuscular ?? null} invertColors />}
                      />
                    )}
                    {latest.masaGrasa != null && (
                      <VitalCard
                        label="Masa grasa" emoji="📉" unit="kg"
                        value={String(latest.masaGrasa)} status="normal"
                        trend={<Trend curr={latest.masaGrasa} prev={prev?.masaGrasa ?? null} />}
                      />
                    )}
                    {latest.masaOsea != null && (
                      <VitalCard
                        label="Masa ósea" emoji="🦴" unit="kg"
                        value={String(latest.masaOsea)} status="normal"
                      />
                    )}
                    {latest.vo2Max != null && (
                      <VitalCard
                        label="VO₂ Máx" emoji="🏃" unit="mL/kg/min"
                        value={String(latest.vo2Max)}
                        status={vo2MaxStatus(Number(latest.vo2Max))}
                        trend={<Trend curr={latest.vo2Max} prev={prev?.vo2Max ?? null} invertColors />}
                        sub={vo2MaxLabel(Number(latest.vo2Max))}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* History */}
          <div>
            <p className="text-[10px] font-bold text-text-secondary uppercase tracking-widest mb-2">
              Historial ({readings.length} {readings.length === 1 ? 'medición' : 'mediciones'})
            </p>
            <div className="space-y-1.5">
              {readings.map((r, i) => (
                <HistoryRow key={r.id} r={r} prev={readings[i + 1] ?? null} />
              ))}
            </div>
          </div>
        </>
      ) : (
        /* ─── Cronológico ─── */
        <div>
          <p className="text-xs text-text-secondary mb-3">
            Vista cronológica — {readings.length} medición{readings.length !== 1 && 'es'}.
            {readings.length < 2 && ' Se necesitan al menos 2 mediciones para mostrar gráficas.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PaChartCard data={chronoData.map(r => ({ fecha: r.fechaHora, s: r.presionSistolica, d: r.presionDiastolica }))} />
            <ChartCard label="Frecuencia Cardíaca" unit="lpm" emoji="❤️" color="#ef4444"
              normalMin={60} normalMax={100}
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.frecuenciaCardiaca }))} />
            <ChartCard label="Temperatura" unit="°C" emoji="🌡️" color="#f97316"
              normalMin={36} normalMax={37.4} domain={[34, 42]}
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.temperatura }))} />
            <ChartCard label="SpO₂" unit="%" emoji="🫁" color="#3b82f6"
              normalMin={95} normalMax={100} domain={[85, 100]}
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.saturacionOxigeno }))} />
            <ChartCard label="Frecuencia Respiratoria" unit="rpm" emoji="💨" color="#8b5cf6"
              normalMin={12} normalMax={20}
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.frecuenciaRespiratoria }))} />
            <ChartCard label="Glucosa" unit="mg/dL" emoji="🩸" color="#ec4899"
              normalMin={70} normalMax={100}
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.glucosa }))} />
            <ChartCard label="Peso" unit="kg" emoji="⚖️" color="#10b981"
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.peso }))} />
            <ChartCard label="IMC" unit="kg/m²" emoji="📊" color="#6366f1"
              normalMin={18.5} normalMax={24.9}
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: calcImc(r.peso, r.talla) }))} />
            {/* Deportivo */}
            <ChartCard label="% Grasa corporal" unit="%" emoji="🔬" color="#7c3aed"
              normalMin={8} normalMax={20}
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.porcentajeGrasaCorporal }))} />
            <ChartCard label="Masa muscular" unit="kg" emoji="💪" color="#059669"
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.masaMuscular }))} />
            <ChartCard label="Masa grasa" unit="kg" emoji="📉" color="#dc2626"
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.masaGrasa }))} />
            <ChartCard label="VO₂ Máx" unit="mL/kg/min" emoji="🏃" color="#0891b2"
              normalMin={45} normalMax={65}
              data={chronoData.map(r => ({ fecha: r.fechaHora, value: r.vo2Max }))} />
          </div>
          {readings.length < 2 && (
            <p className="text-center py-8 text-sm text-text-secondary">
              Registra más mediciones para visualizar tendencias en el tiempo
            </p>
          )}
        </div>
      )}

      {modalOpen && (
        <NuevaMedicionModal
          onClose={() => setModalOpen(false)}
          onSave={dto => createMutation.mutate(dto)}
          puedeVerDeportivo={puedeVerDeportivo}
          isPending={createMutation.isPending}
        />
      )}
    </div>
  );
}
